import { hub } from 'services/hub';
import { bugsnagNotify } from 'containers/ErrorBoundary/utils';
import { toast, TOAST_TYPE } from 'components/Toast';

/**
 * Creates a new scene/rule on the specified hub via WebSocket API.
 *
 * @param {string} serial - Hub serial number (e.g., "90004631")
 * @param {Object} rule - Scene/rule configuration object
 * @param {boolean} rule.enabled - Whether the rule is enabled
 * @param {string} rule.name - Display name for the rule
 * @param {Array} rule.when - Array of trigger blocks
 * @param {Array} rule.then - Array of action blocks
 * @param {Array} rule.else - Array of else action blocks
 * @param {Array} rule.exceptions - Array of exception blocks
 * @param {string} [rule.exec_policy] - Execution policy ('wait_result', 'ignore_result', 'check_result')
 * @param {Array<string>} rule.house_modes - Array of house mode IDs
 * @param {Object} [rule.meta] - Additional metadata
 *
 * @returns {Promise<Object>} Promise resolving to API response object
 * @returns {string} returns.id - Unique request ID
 * @returns {string} returns.method - API method name ('hub.scenes.create')
 * @returns {*} returns.result - API result (null for create operations)
 * @returns {*} returns.error - Error object if request failed
 *
 * @throws {Error} API error with error message displayed as toast
 *
 * @example
 * const rule = {
 *   enabled: true,
 *   name: "Turn on lights",
 *   when: [{ blockType: "when", ... }],
 *   then: [{ blockType: "then", ... }],
 *   house_modes: ["1", "2", "3", "4"]
 * };
 *
 * try {
 *   const response = await hubScenesCreate("90004631", rule);
 *   console.log('Scene created with ID:', response.id);
 * } catch (error) {
 *   console.error('Failed to create scene:', error);
 * }
 */
export const hubScenesCreate = (serial, rule) =>
    new Promise((resolve, reject) => {
        hub.scenes.create(serial, rule, resolve, (error) => {
            bugsnagNotify(error, { serial, rule });
            toast(error.message, { type: TOAST_TYPE.ERROR });
            reject(error);
        });
    });
